import { Button, SvgIcon, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Stack } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import {
  pullUuidAction,
  resetForLoaderDocumentation,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import Loader from 'src/components/LoaderImg/loader.gif';
import useCurrentUser from 'src/hooks/use-current-user';
import DocumentTemplateUnavailable from 'src/components/document-management/document-template-unavailable';
import ScreeningForm from 'src/components/ScreeningForm';
import patientScreeningService from 'src/services/apiServices/screeningDetails/screeningDetails';

function EditScreeningDocumentPreview() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const previeWScreeningData = useSelector(
    (state: any) => state.getScreeningReducer?.previewScreeningData
  );
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const [discipline] = useCurrentUser(['discipline']);
  const formIdOfScreening = useSelector((state: any) => state.getFormIdForScreeningReducer);

  // JOTFORM-DISCONNECT(BRK-1926): screenings submitted natively carry their raw ReForm JSON in
  // S3 (surfaced as `submissionDataAvailable` on the list row) and are edited in the Reform
  // ScreeningForm. Legacy Jotform-era rows have no stored JSON, and Jotform is retired, so they
  // can no longer be edited.
  const isNativeEdit = !!previeWScreeningData?.submissionDataAvailable;
  const [nativeSubmissionData, setNativeSubmissionData] = useState<any | null>(null);
  const [nativeLoadError, setNativeLoadError] = useState<string | null>(null);

  const disciplineString = typeof discipline === 'string' ? discipline : '';

  // JOTFORM-DISCONNECT(BRK-1926): native edit prefill — the raw ReForm JSON of the submission
  // being edited, read back from S3 through provider-service.
  useEffect(() => {
    if (!isNativeEdit || !previeWScreeningData?.uuid || !getPatientId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await patientScreeningService.getScreeningSubmissionData({
          patientId: getPatientId,
          screeningId: previeWScreeningData.uuid,
        });
        if (cancelled) return;
        const submissionData = response?.data?.data;
        if (submissionData) {
          setNativeSubmissionData(submissionData);
        } else {
          setNativeLoadError('The stored screening data could not be loaded.');
        }
      } catch (error: any) {
        console.error('Error loading screening submission data:', error);
        if (!cancelled) {
          setNativeLoadError(
            error?.response?.data?.message || 'The stored screening data could not be loaded.'
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNativeEdit, previeWScreeningData, getPatientId]);

  // The native edit submits against a SCREENING webhook minted for THIS screening (screeningId
  // carries the edit context through document-service to provider-service).
  useEffect(() => {
    if (!previeWScreeningData || !isNativeEdit) return;
    dispatch(
      pullUuidAction({
        patientId: getPatientId,
        entityType: 'SCREENING',
        screeningId: previeWScreeningData?.uuid,
        formId: formIdOfScreening?.response,
        isCompleted: false,
      })
    );
  }, [previeWScreeningData, isNativeEdit]);

  const goToBack = () => {
    dispatch(resetUuidAction());
    dispatch(resetForLoaderDocumentation());
    navigate('/patients/patient-details/screening');
  };

  let editContent;
  if (isNativeEdit) {
    if (nativeLoadError) {
      editContent = (
        <Typography
          variant="body1"
          sx={{ margin: '2rem auto', textAlign: 'center' }}
        >
          {nativeLoadError}
        </Typography>
      );
    } else if (nativeSubmissionData && pullUuidResponse.response) {
      editContent = (
        <ScreeningForm
          prefill={nativeSubmissionData}
          discipline={disciplineString}
          webhookUuid={pullUuidResponse.response?.trim()}
          formId={formIdOfScreening?.response ?? ''}
          onDone={goToBack}
        />
      );
    } else {
      editContent = (
        <img
          src={Loader}
          alt="loader"
          style={{ width: '10%', margin: 'auto' }}
        />
      );
    }
  } else if (previeWScreeningData) {
    editContent = (
      <Stack sx={{ padding: '1.5rem 4rem' }}>
        <DocumentTemplateUnavailable
          onBack={goToBack}
          backLabel="Back to Screening List"
          reason="legacy"
        />
      </Stack>
    );
  }

  return (
    <>
      <Stack
        gap="1.5rem"
        sx={{ padding: '0 4rem' }}
      >
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
          onClick={goToBack}
        >
          <Typography variant="subtitle1">Back to Screening List</Typography>
        </Button>
        <Stack
          flexDirection="row"
          gap="1rem"
          alignItems="center"
        >
          <Typography variant="h5">Edit Document</Typography>
        </Stack>
      </Stack>

      {editContent}
    </>
  );
}

export default EditScreeningDocumentPreview;
